/**
 * GBPChannelTile — Google Business Profile tile for the dashboard channel row
 *
 * Shows views sparkline when connected, "Connect" CTA when not connected.
 * Matches the visual pattern of ChannelTile but with GBP-specific data.
 */

import { Link } from "react-router-dom"
import { ArrowRight } from "lucide-react"
import { cn } from "@/lib/utils"
import { useGBPDashboardSummary } from "../api/gbpHooks"
import { PulseSparkline } from "@/features/dashboard/components/PulseSparkline"
import type { SparklinePoint } from "@/hooks/usePulseSparkline"

/** Google "G" logo — multi-color */
function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} xmlns="http://www.w3.org/2000/svg">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  )
}

const GOOGLE_BLUE = "#4285F4"

export function GBPChannelTile() {
  const { data: summary } = useGBPDashboardSummary()

  const isConnected = summary?.connected ?? false
  const isVerified = summary?.verified ?? false
  const hasData = isConnected && isVerified

  const sparklineData: SparklinePoint[] =
    hasData && summary?.sparkline_7d
      ? summary.sparkline_7d.map((value) => ({ date: "", value }))
      : []

  return (
    <Link
      to="/google-business"
      className={cn(
        "relative min-w-0 rounded-md p-4 overflow-hidden",
        "bg-white dark:bg-gray-900",
        "border border-gray-100 dark:border-gray-800",
        "transition-all duration-200",
        "hover:scale-[1.02] hover:shadow-lg active:scale-[0.98]",
      )}
      aria-label={
        hasData
          ? `Google Business — ${summary?.views_total ?? 0} views this week`
          : "Google Business — Connect"
      }
    >
      <div className="relative z-10 flex flex-col min-h-[120px]">
        <div className="flex items-center gap-2">
          <GoogleIcon className="w-5 h-5 flex-shrink-0" />
          <p className="text-[12px] font-semibold tracking-wide text-gray-700 dark:text-gray-300">
            Google Business
          </p>
        </div>

        {hasData ? (
          <>
            <p className="text-[22px] font-semibold text-gray-900 dark:text-gray-100 mt-2">
              {(summary?.views_total ?? 0).toLocaleString()}
              <span className="text-[12px] font-normal text-gray-500 ml-1">
                views
              </span>
            </p>
            <p className="text-[13px] text-gray-500 dark:text-gray-400">
              {summary?.location_name ?? ""}
            </p>
          </>
        ) : (
          <>
            <p className="text-[13px] font-medium text-gray-700 dark:text-gray-300 leading-snug mt-2">
              {isConnected ? "รอการยืนยัน" : "เชื่อมต่อ Google Business"}
            </p>
            <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">
              {isConnected
                ? "ยืนยันธุรกิจเพื่อดูข้อมูล"
                : "ดูว่าลูกค้าค้นหาคุณอย่างไร"}
            </p>
          </>
        )}

        <div className="mt-auto">
          <span
            className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] font-medium text-white"
            style={{ backgroundColor: GOOGLE_BLUE }}
          >
            {hasData ? "ดูรายละเอียด" : "เชื่อมต่อ"}
            <ArrowRight className="w-3 h-3" />
          </span>
        </div>
      </div>

      <div className="absolute inset-0">
        <PulseSparkline
          data={sparklineData}
          isZero={!hasData}
          metric="orders"
        />
      </div>
    </Link>
  )
}
